import { useState } from 'react'
import { Heading, Subheading } from '../catalyst/heading'
import { RadioCards } from '../catalyst/radio-cards'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import type { OrgMembership } from '../lib/types'
import { Shell } from './shell'

// The organization's settings. For now only how units are shown: every amount is stored metric, so
// switching changes what people see and type by default, never the records themselves.

type UnitSystem = OrgMembership['unitSystem']

export function SettingsPage() {
  const { org, setUnitSystem } = useAuth()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (!org) return null
  const canEdit = org.role === 'OWNER'

  async function choose(system: UnitSystem) {
    if (system === org?.unitSystem) return
    setSaving(true)
    setError(null)
    try {
      await setUnitSystem(system)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save it.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Shell>
      <div className="mx-auto max-w-2xl">
        <Heading>Settings</Heading>
        <Text>{org.name}</Text>

        <Subheading className="mt-10">Units</Subheading>
        <Text>
          How areas, yields and stock amounts are shown across Farmula. Records are stored the same either way, so you can switch at any time. A stock item can
          also have its own unit.
        </Text>
        <div className={saving ? 'pointer-events-none mt-6 opacity-60' : 'mt-6'}>
          {canEdit ? (
            <RadioCards<UnitSystem>
              label="Show amounts in"
              value={org.unitSystem}
              onChange={choose}
              options={[
                { value: 'IMPERIAL', label: 'Imperial', detail: 'Acres, lb/ac, US gallons, pounds, cubic yards' },
                { value: 'METRIC', label: 'Metric', detail: 'Hectares, kg/ha, litres, kilograms, cubic metres' },
              ]}
            />
          ) : (
            <Text>
              Amounts are shown in <strong>{org.unitSystem === 'METRIC' ? 'metric' : 'imperial'}</strong> units. The owner can change this.
            </Text>
          )}
        </div>
        {error && <Text className="mt-4 text-red-600 dark:text-red-400">{error}</Text>}
      </div>
    </Shell>
  )
}
